import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
// eslint-disable-next-line import/no-cycle
import BlockBuilder from '../../BlockBuilder';
import breakpoints from '../../../breakpoints';
import sharedStyles from '../../../styles';

const Container = styled.div`
  margin-block: ${sharedStyles.spacing['spacing-md']};

  // Scroll position to respect sticky banner
  scroll-margin-top: 50px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: ${({ position }) =>
    position === 'top' ? 'column-reverse' : 'column'};
`;

const InstructionsContainer = styled.div`
  margin-block: ${sharedStyles.spacing['spacing-md']};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  word-break: break-word;
  overflow-y: scroll;

  td:first-child,
  th:first-child {
    display: none;
  }

  td,
  th {
    text-align: center;
    padding: ${sharedStyles.spacing['spacing-1xs']};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder}; // Should use different theme value here?
  }

  && .always-visible-cell {
    display: table-cell;
  }

  @media (min-width: ${breakpoints.md}) {
    td:first-child,
    th:first-child {
      display: table-cell;
      font-weight: bold;
    }
  }
`;

const MobileTableRow = styled.tr`
  display: table-row;
  color: ${({ theme }) =>
    theme.colors.cardText}; // Should use different theme value here?
  background: ${({ theme }) =>
    theme.colors.inputBorder}; // Should use different theme value here?
  font-weight: bold;

  td:first-child {
    display: table-cell;
  }

  @media (min-width: ${breakpoints.md}) {
    display: none;
  }
`;

const TableCell = styled.td`
  width: ${props => (props.cellCount ? 100 / props.cellCount - 1 : 100)}%;
`;

function BlockBuilderSizeChart({ value }) {
  const cellsCount = value?.table?.rows?.[0]?.cells?.length || 0;

  return (
    <Container id="SizeChart">
      {value?.title && <h2>{value.title}</h2>}
      <Wrapper position={value?.instructionsPosition || 'top'}>
        {value?.table && (
          <Table>
            <thead>
              {value?.header && (
                <tr>
                  <th className="always-visible-cell" colSpan={cellsCount}>
                    {value.header}
                  </th>
                </tr>
              )}
              <tr>
                {value.table?.rows?.[0]?.cells?.map(cell => (
                  <th key={`cell-${cell}`}>{cell}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {value.table?.rows?.map((row, index) => {
                if (index !== 0) {
                  return (
                    <Fragment key={row._key}>
                      <MobileTableRow>
                        <td colSpan={cellsCount}>{row.cells[0]}</td>
                      </MobileTableRow>
                      <tr>
                        {row.cells.map(cell => (
                          <TableCell
                            key={`rowcell-${cell}`}
                            cellCount={row.cells?.length}
                          >
                            {cell}
                          </TableCell>
                        ))}
                      </tr>
                    </Fragment>
                  );
                }
                return null;
              })}
            </tbody>

            {value?.footer && (
              <tfoot>
                <tr>
                  <th className="always-visible-cell" colSpan={cellsCount}>
                    {value.footer}
                  </th>
                </tr>
              </tfoot>
            )}
          </Table>
        )}
        {value?.instructions && (
          <InstructionsContainer>
            <BlockBuilder blocks={value.instructions} />
          </InstructionsContainer>
        )}
      </Wrapper>
    </Container>
  );
}

BlockBuilderSizeChart.propTypes = {
  value: PropTypes.shape({
    title: PropTypes.string,
    table: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          cells: PropTypes.arrayOf(PropTypes.string),
        })
      ),
    }),
    header: PropTypes.string,
    footer: PropTypes.string,
    instructions: PropTypes.arrayOf(PropTypes.shape({})),
    instructionsPosition: PropTypes.string,
  }).isRequired,
};

export default BlockBuilderSizeChart;
